import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { searchResultsPropTypes } from './SearchResultsPropTypes';
import SearchResultsProducts from './components/SearchResultsProducts/SearchResultsProducts';
import productsData from './components/SearchResultsProducts/SearchResultsProductsData';
// import SearchResultsContent from './components/SearchResultsContent/SearchResultsContent';
import Icon from '@Theme/natucare/components/Icon/Icon';
import useEscapeKeyDown from '@App/common/components/Modal/hooks/useEscapeKeyDown';
import { KEYBOARD_KEY } from '@App/common/constants/keyboardKey';
import * as styles from './SearchResults.module.scss';
const constructFormData = (searchPhrase, searchTarget = null) => {
  const data = {
    search_phrase: searchPhrase,
    search_target: searchTarget,
    site_id: 2,
    request_uri: window.location.href
  };
  const formData = new FormData();
  Object.keys(data).forEach(key => formData.set(key, data[key]));
  return formData;
};
const SearchResults = /*#__PURE__*/forwardRef(({
  searchResults,
  setIsSearching,
  isSearching,
  searchTerm,
  sendSearchData,
  placeholder,
  onChange,
  popularSearches
}, ref) => {
  var _remoteProducts$items, _effectiveProducts$it;
  const rootRef = useRef();
  const inputRef = useRef();
  const isFirstRender = useRef(true);
  const {
    0: inputValue,
    1: setInputValue
  } = useState(searchTerm || '');
  const {
    0: isInputActive,
    1: setIsInputActive
  } = useState(false);
  const {
    0: hasSubmitted,
    1: setHasSubmitted
  } = useState(false);
  useEscapeKeyDown(isSearching, () => setIsSearching(false));
  useImperativeHandle(ref, () => ({
    focusInput: () => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }
  }), []);
  useEffect(() => {
    if (isSearching && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isSearching]);
  useEffect(() => {
    setInputValue(searchTerm || '');
    if (!searchTerm) {
      setHasSubmitted(false);
    }
  }, [searchTerm]);
  const handleClose = useCallback(() => {
    setIsInputActive(false);
    setIsSearching(false);
    setHasSubmitted(false);
  }, [setIsSearching]);
  useEffect(() => {
    if (!isSearching) {
      setIsInputActive(false);
      setHasSubmitted(false);
    }
  }, [isSearching]);
  const handleClick = useCallback(e => {
    const clickedAnchor = e.target.closest('a');
    if (clickedAnchor) {
      const clickedAnchorHref = clickedAnchor.getAttribute('href');
      const formData = constructFormData(searchTerm, clickedAnchorHref);
      sendSearchData === null || sendSearchData === void 0 ? void 0 : sendSearchData(formData);
    }
  }, [searchTerm, sendSearchData]);
  useEffect(() => {
    if (rootRef.current) {
      rootRef.current.scrollTop = 0;
    }
    if (!isFirstRender.current) {
      if (typeof window !== 'undefined' && isSearching === false) {
        const formData = constructFormData(searchTerm);
        sendSearchData === null || sendSearchData === void 0 ? void 0 : sendSearchData(formData);
      }
    } else {
      isFirstRender.current = false;
    }
  }, [isSearching, searchTerm, sendSearchData]);
  useEffect(() => {
    const rootElement = rootRef.current;
    if (rootElement) {
      rootElement.addEventListener('click', handleClick);
    }
    return () => {
      if (rootElement) {
        rootElement.removeEventListener('click', handleClick);
      }
    };
  }, [handleClick]);
  const handleSearch = useCallback(value => {
    const trimmedValue = value.trim();
    setIsInputActive(true);
    setIsSearching(true);
    if (trimmedValue === '') {
      setHasSubmitted(false);
      onChange === null || onChange === void 0 ? void 0 : onChange('');
      return;
    }
    setHasSubmitted(true);
    onChange === null || onChange === void 0 ? void 0 : onChange(trimmedValue);
  }, [onChange, setIsSearching]);
  const handleInputChange = event => {
    const {
      value
    } = event.target;
    setInputValue(value);
  };
  const handleKeyDown = event => {
    if (event.key === KEYBOARD_KEY.ENTER) {
      handleSearch(event.target.value);
    }
  };
  const status = searchResults.status || {};
  const isFetching = hasSubmitted && Boolean(status.isFetching && !status.isLoaded && !searchResults.stringNotFound);
  const fallbackProducts = productsData.createFromQuery(inputValue);
  const remoteProducts = searchResults.products;
  const effectiveProducts = remoteProducts !== null && remoteProducts !== void 0 && (_remoteProducts$items = remoteProducts.items) !== null && _remoteProducts$items !== void 0 && _remoteProducts$items.length ? remoteProducts : fallbackProducts;
  const hasProducts = Boolean(effectiveProducts === null || effectiveProducts === void 0 ? void 0 : (_effectiveProducts$it = effectiveProducts.items) === null || _effectiveProducts$it === void 0 ? void 0 : _effectiveProducts$it.length);
  const shouldShowNotFound = hasSubmitted && !isFetching && !hasProducts && inputValue.trim().length > 0;
  const queryLabel = searchResults.stringNotFound || inputValue;
  return /*#__PURE__*/React.createElement("div", {
    className: `${styles.root} ${isSearching ? styles.rootSearching : ''}`,
    ref: rootRef,
    "data-scroll-lock-exempt": true
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.rootContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.rootHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.rootInputWrapper
  }, /*#__PURE__*/React.createElement("input", {
    ref: inputRef,
    type: "search",
    placeholder: placeholder || 'Czego szukasz?',
    value: inputValue,
    onChange: handleInputChange,
    onKeyDown: handleKeyDown,
    className: styles.rootInput
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: styles.rootSubmit,
    onClick: () => handleSearch(inputValue)
  }, "Szukaj"))), /*#__PURE__*/React.createElement("div", {
    className: styles.rootBody
  }, isFetching && /*#__PURE__*/React.createElement(Icon, {
    name: "loaderDotsAnimation",
    size: {
      width: 100,
      height: 100
    },
    classname: styles.rootIconLoading
  }), shouldShowNotFound && /*#__PURE__*/React.createElement("div", {
    className: styles.rootNotFound
  }, `${searchResults.notFoundDescription || 'Przepraszamy, brak wyników dla słowa'} ${queryLabel}`), /*#__PURE__*/React.createElement(SearchResultsProducts, effectiveProducts), (!hasSubmitted || shouldShowNotFound) && /*#__PURE__*/React.createElement("div", {
    className: styles.rootPopular
  }, /*#__PURE__*/React.createElement("p", {
    className: styles.rootPopularHeading
  }, "Najcz\u0119\u015Bciej wyszukiwane"), /*#__PURE__*/React.createElement("div", {
    className: styles.rootPopularList
  }, /*#__PURE__*/React.createElement("a", {
    href: "https://shop.natu.care/collections#kolagen",
    className: styles.rootPopularButton
  }, "Kolagen"), /*#__PURE__*/React.createElement("a", {
    href: "https://shop.natu.care/collections#omega-3",
    className: styles.rootPopularButton
  }, "Omega-3"), "                  ", /*#__PURE__*/React.createElement("a", {
    href: "https://shop.natu.care/collections#maslan-sodu",
    className: styles.rootPopularButton
  }, "Ma\u015Blan sodu"))))));
});
SearchResults.displayName = 'SearchResults';
export default SearchResults;